import React, { useEffect, useCallback, useState } from 'react'
import Head from 'next/head'
import ReactMarkdown from 'react-markdown'
import dayjs from 'dayjs'
import Icon from '@mdi/react'
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { useProfile } from 'hooks/user'
import { track } from 'helpers/Analytics'
import { usePagingState } from 'hooks/base'
import { requestArticle, requestArticleComments } from 'api/document'
import Image from 'components/common/Image'
import EmptyView from 'components/common/Empty'
import CommentItem from 'components/modules/comment/Item'
import CommentForm from 'components/modules/comment/Form'
import 'github-markdown-css/github-markdown-light.css'

const Comments = React.memo((props) => {
  const currentUser = useProfile()
  const [replyTo, setReplyTo] = useState(null)

  const { id: articleId, commentable, commentOpening } = props.article

  const { pagingState, reloadCurrentPage, switchToNextPage, switchToPrevPage } = usePagingState({
    dataLoader: (params) => requestArticleComments(articleId, params),
    initialState: { pageSize: 10 },
  })

  const handleSubmitted = useCallback(() => {
    pagingState.page === 1 && reloadCurrentPage()
  }, [pagingState.page])

  const handleReply = useCallback((event) => {
    const { id, username } = event.currentTarget.dataset
    setReplyTo({ id: id * 1, username })
  }, [])

  const handleCancelReply = useCallback(() => {
    setReplyTo(null)
  }, [])

  const isEmpty = !!pagingState.error || (pagingState.loaded && !pagingState.count)
  const isLoading = pagingState.loading && !pagingState.count
  const totalPages = Math.ceil(pagingState.count / pagingState.pageSize)

  const showComments = commentable
  const showForm = currentUser.id && commentable

  return (
    <div className="mx-auto mt-4 bg-white lg:w-800 xl:w-900">
      <div className="flex flex-col items-stretch px-4 py-10 lg:mx-auto lg:w-800 lg:px-8 lg:py-8 xl:py-16">
        <div className="flex flex-col items-center justify-between lg:flex-row lg:items-end">
          <h2 className="text-base font-medium text-gray-600 lg:text-xl">文章评论</h2>
          <h3 className="text-xs text-gray-400 lg:text-tiny">{showComments ? `共${pagingState.count}条评论` : '文章已关闭评论'}</h3>
        </div>
        {showComments && !commentOpening && (
          <div className="mx-auto mt-4 rounded bg-gray-50 p-4 text-center text-tiny text-gray-400">
            <span>由于作者设置，你的评论仅对你自己和作者可见</span>
          </div>
        )}
        <div className="mx-auto mt-6 flex w-full flex-1 flex-col overflow-auto lg:mt-10" data-empty={isEmpty}>
          {isEmpty ? (
            <EmptyView className="m-auto py-20" type={showComments ? 'default' : 'locked'} caption={showForm ? '暂无评论' : '文章已关闭评论'} />
          ) : (
            pagingState.items.map((item) => {
              return <CommentItem key={item.id} allowReply data={item} meId={currentUser.id} isReplying={replyTo?.id === item.id} onReply={handleReply} onCancelReply={handleCancelReply} />
            })
          )}
        </div>
        {!isEmpty && !isLoading && (
          <div className="flex items-center justify-center self-stretch">
            <button
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded bg-gray-100 text-gray-500 transition-colors hover:bg-gray-300/80 disabled:opacity-30"
              disabled={pagingState.page <= 1}
              onClick={switchToPrevPage}
            >
              <Icon size={1} path={mdiChevronLeft} />
            </button>
            <span className="mx-4 text-tiny text-gray-500">
              {pagingState.page} / {totalPages}
            </span>
            <button
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded bg-gray-100 text-gray-500 transition-colors hover:bg-gray-300/80 disabled:opacity-30"
              disabled={pagingState.page >= totalPages}
              onClick={switchToNextPage}
            >
              <Icon size={1} path={mdiChevronRight} />
            </button>
          </div>
        )}
        {showForm && <CommentForm className="mt-6 lg:mt-12" type="article" targetId={articleId} replyTo={replyTo} onSubmitted={handleSubmitted} onCancelReply={handleCancelReply} />}
      </div>
    </div>
  )
})

const Article = React.memo((props) => {
  const profile = useProfile()
  const [article, setArticle] = useState(props.article)
  const [loading, setLoading] = useState(article.protected)

  useEffect(() => {
    if (profile.initialized) {
      if (article.protected) {
        setLoading(true)
        requestArticle(article.id)
          .then((res) => {
            setLoading(false)
            if (res.data && res.data.content) {
              setArticle(res.data)
            }
          })
          .catch(console.warn)
      }

      track('viewArticle', {
        userId: profile.id || 'unknow',
        articleId: article.id,
      })
    }
  }, [profile.initialized])

  if (loading) return null

  return (
    <article className="flex flex-col items-stretch lg:items-center">
      <Head>
        <title>{article.title} | 简约简历</title>
      </Head>
      <header className="relative w-full">
        <div className="absolute top-0 right-0 bottom-0 left-0 z-0 hidden bg-gray-200 lg:block" />
        <div className="relative z-1 mx-auto text-center xl:w-900">
          <h1 className="mt-10 px-6 text-2xl font-medium text-gray-600 lg:mt-20 lg:px-0 lg:text-4xl">{article.title}</h1>
          <div className="mt-4">
            <span className="text-sm text-gray-500">发布于 {dayjs(article.createdAt).fromNow()}</span>
          </div>
        </div>
        {!!article.cover?.data ? (
          <Image className="relative z-2 mx-auto mt-6 h-auto bg-gray-500 lg:mt-10 lg:w-800 xl:h-[350px] xl:w-900" src={article.cover.data.url} crop="1800x750" />
        ) : (
          <div className="relative z-2 mx-auto mt-6 w-900" />
        )}
      </header>
      <div className="markdown-body w-full lg:w-800 xl:w-900">
        <div className="px-6 py-10 text-[15px] leading-6 text-gray-500 lg:mx-auto lg:w-800 lg:px-10 xl:px-0 xl:py-16">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>
      </div>
    </article>
  )
})

const PageArticle = (props) => {
  const { article } = props

  return (
    <div className="flex-1 border-b border-gray-200 pb-4 lg:pb-16">
      <Head>
        <title>{article.protected ? '内容加载中' : article.title} | 简约简历</title>
      </Head>
      <Article article={article} />
      <Comments article={article} />
    </div>
  )
}

export const getServerSideProps = async (ctx) => {
  const { id } = ctx.query

  try {
    const article = await requestArticle(id, {
      headers: ctx.req?.headers?.cookie ? { cookie: ctx.req.headers.cookie } : undefined,
    })

    return { props: { article: article.data } }
  } catch (error) {
    // console.log(error)
    return { notFound: true }
  }
}

PageArticle.options = {
  ssr: true,
  title: '文章',
  layoutClassName: 'bg-gray-100 h-screen',
}

export default PageArticle
